import { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Menu, X, ChevronDown } from 'lucide-react'
import { LanguageSelector } from '@/components/LanguageSelector'
import { LocalizedLink } from '@/components/LocalizedLink'
import { getMediaUrl } from '@/utils/media'
import type { SupportedLanguage } from '@/i18n'

interface NavItem {
  label: string
  href: string
  children?: NavItem[]
}

const PAGES_WITH_HERO = ['', 'about', 'cathodic-protection', 'oxygen-evolution', 'electrochlorination', 'hiseochlor', 'chlorine-alkali']

export function Header() {
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const currentLang = i18n.language as SupportedLanguage
  const currentPath = location.pathname.replace(`/${currentLang}`, '').replace('/', '')
  const hasHero = PAGES_WITH_HERO.includes(currentPath)
  const isTransparent = hasHero && !isScrolled && !isMenuOpen

  const navItems: NavItem[] = [
    { label: t('nav.about'), href: '/about' },
    {
      label: t('nav.products'),
      href: '/cathodic-protection',
      children: [
        { label: t('nav.cathodicProtection'), href: '/cathodic-protection' },
        { label: t('nav.oxygenEvolution'), href: '/oxygen-evolution' },
        { label: t('nav.electrochlorination'), href: '/electrochlorination' },
        { label: t('nav.hiseochlor'), href: '/hiseochlor' },
        { label: t('nav.chlorineAlkali'), href: '/chlorine-alkali' },
      ],
    },
    { label: t('nav.certifications'), href: '/certifications' },
    { label: t('nav.contact'), href: '/contact' },
  ]

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setIsMenuOpen(false)
    setOpenDropdown(null)
  }, [location.pathname])

  const isActive = (href: string) => {
    const path = location.pathname.replace(`/${currentLang}`, '')
    return path === href || path.startsWith(href + '/')
  }

  const handleMouseEnter = (label: string) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
    }
    setOpenDropdown(label)
  }

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setOpenDropdown(null)
    }, 100)
  }

  return (
    <>
      {/* Dropdown Styles */}
      <style>{`
        .nav-dropdown {
          opacity: 0;
          visibility: hidden;
          transform: translateY(10px) translateX(-50%);
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .nav-dropdown.open {
          opacity: 1;
          visibility: visible;
          transform: translateY(0) translateX(-50%);
        }
        .dropdown-item {
          position: relative;
          transition: all 0.15s ease;
        }
        .dropdown-item:hover {
          background-color: rgba(0, 163, 204, 0.08);
        }
        .dropdown-item::before {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%) scaleY(0);
          width: 3px;
          height: 24px;
          background: linear-gradient(180deg, #00a3cc 0%, #0077b3 100%);
          border-radius: 0 4px 4px 0;
          transition: transform 0.2s ease;
        }
        .dropdown-item:hover::before,
        .dropdown-item.active::before {
          transform: translateY(-50%) scaleY(1);
        }
        .dropdown-item.active {
          background-color: rgba(0, 163, 204, 0.06);
        }
        .mobile-menu {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.3s ease;
        }
        .mobile-menu.open {
          max-height: 100vh;
        }
        .mobile-submenu {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.25s ease;
        }
        .mobile-submenu.open {
          max-height: 500px;
        }
      `}</style>

      <header
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          top: 0,
          zIndex: 1000,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          backgroundColor: isTransparent ? 'rgba(0, 59, 122, 0.92)' : '#ffffff',
          boxShadow: isTransparent ? 'none' : '0 1px 0 rgba(0,0,0,0.05), 0 4px 20px rgba(0,0,0,0.08)',
          backdropFilter: 'blur(16px)',
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <nav style={{ display: 'flex', height: '72px', alignItems: 'center', justifyContent: 'space-between' }}>
            {/* Logo */}
            <Link to={`/${currentLang}`} aria-label="Chemical Newtech - Home">
              <img
                src={getMediaUrl('logos/chemical-newtech-logo.png')}
                alt="Chemical Newtech S.p.A."
                style={{ height: '42px', width: 'auto' }}
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex" style={{ alignItems: 'center', gap: '4px' }}>
              {navItems.map((item) => (
                <div
                  key={item.label}
                  style={{ position: 'relative' }}
                  onMouseEnter={() => item.children && handleMouseEnter(item.label)}
                  onMouseLeave={handleMouseLeave}
                >
                  {item.children ? (
                    <>
                      <button
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '10px 16px',
                          fontSize: '15px',
                          fontWeight: 500,
                          color: isTransparent ? '#ffffff' : '#1e293b',
                          background: openDropdown === item.label
                            ? (isTransparent ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)')
                            : 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          borderRadius: '8px',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {item.label}
                        <ChevronDown
                          size={16}
                          style={{
                            opacity: 0.6,
                            transition: 'transform 0.25s ease',
                            transform: openDropdown === item.label ? 'rotate(180deg)' : 'rotate(0)',
                          }}
                        />
                      </button>

                      {/* Dropdown */}
                      <div
                        className={`nav-dropdown ${openDropdown === item.label ? 'open' : ''}`}
                        style={{
                          position: 'absolute',
                          left: '50%',
                          top: 'calc(100% + 12px)',
                          minWidth: '260px',
                          backgroundColor: '#ffffff',
                          borderRadius: '16px',
                          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.03)',
                          padding: '8px',
                          zIndex: 1001,
                        }}
                      >
                        {/* Arrow */}
                        <div
                          style={{
                            position: 'absolute',
                            top: '-8px',
                            left: '50%',
                            transform: 'translateX(-50%) rotate(45deg)',
                            width: '16px',
                            height: '16px',
                            backgroundColor: '#ffffff',
                            boxShadow: '-2px -2px 4px rgba(0,0,0,0.04)',
                            borderRadius: '3px 0 0 0',
                          }}
                        />

                        <div style={{ position: 'relative', zIndex: 1 }}>
                          {item.children.map((child) => (
                            <LocalizedLink
                              key={child.href}
                              to={child.href}
                              className={`dropdown-item ${isActive(child.href) ? 'active' : ''}`}
                              style={{
                                display: 'block',
                                padding: '14px 20px 14px 16px',
                                fontSize: '14px',
                                fontWeight: isActive(child.href) ? 600 : 450,
                                color: isActive(child.href) ? '#003b7a' : '#334155',
                                textDecoration: 'none',
                                borderRadius: '10px',
                                margin: '2px 0',
                              }}
                            >
                              {child.label}
                            </LocalizedLink>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <LocalizedLink
                      to={item.href}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 16px',
                        fontSize: '15px',
                        fontWeight: isActive(item.href) ? 600 : 500,
                        color: isTransparent
                          ? '#ffffff'
                          : isActive(item.href)
                            ? '#003b7a'
                            : '#1e293b',
                        textDecoration: 'none',
                        borderRadius: '8px',
                        transition: 'all 0.2s ease',
                        position: 'relative',
                      }}
                    >
                      {item.label}
                      {isActive(item.href) && !isTransparent && (
                        <span
                          style={{
                            position: 'absolute',
                            bottom: '4px',
                            left: '16px',
                            right: '16px',
                            height: '2px',
                            background: 'linear-gradient(90deg, #00a3cc 0%, #0077b3 100%)',
                            borderRadius: '2px',
                          }}
                        />
                      )}
                    </LocalizedLink>
                  )}
                </div>
              ))}
            </div>

            {/* Right side */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <LanguageSelector isScrolled={!isTransparent} />

              <button
                className="lg:hidden flex items-center justify-center"
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  border: 'none',
                  background: isTransparent ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)',
                  color: isTransparent ? '#ffffff' : '#1e293b',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </nav>
        </div>

        {/* Mobile Navigation */}
        <div
          className={`mobile-menu ${isMenuOpen ? 'open' : ''} lg:hidden`}
          style={{
            backgroundColor: '#ffffff',
            borderTop: '1px solid #f1f5f9',
          }}
        >
          <div style={{ padding: '8px 16px 24px' }}>
            {navItems.map((item) => (
              <div key={item.label}>
                {item.children ? (
                  <>
                    <button
                      style={{
                        display: 'flex',
                        width: '100%',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '16px 12px',
                        fontSize: '16px',
                        fontWeight: 500,
                        color: '#1e293b',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        borderRadius: '12px',
                        transition: 'background 0.2s ease',
                      }}
                      onClick={() => setOpenDropdown(openDropdown === item.label ? null : item.label)}
                    >
                      {item.label}
                      <ChevronDown
                        size={20}
                        style={{
                          color: '#64748b',
                          transition: 'transform 0.25s ease',
                          transform: openDropdown === item.label ? 'rotate(180deg)' : 'rotate(0)',
                        }}
                      />
                    </button>

                    <div
                      className={`mobile-submenu ${openDropdown === item.label ? 'open' : ''}`}
                      style={{
                        marginLeft: '12px',
                        borderLeft: '2px solid #e2e8f0',
                        paddingLeft: '12px',
                        marginBottom: openDropdown === item.label ? '8px' : '0',
                      }}
                    >
                      {item.children.map((child) => (
                        <LocalizedLink
                          key={child.href}
                          to={child.href}
                          style={{
                            display: 'block',
                            padding: '12px',
                            fontSize: '15px',
                            color: isActive(child.href) ? '#003b7a' : '#64748b',
                            fontWeight: isActive(child.href) ? 600 : 400,
                            textDecoration: 'none',
                            borderRadius: '8px',
                            backgroundColor: isActive(child.href) ? 'rgba(0, 163, 204, 0.08)' : 'transparent',
                          }}
                        >
                          {child.label}
                        </LocalizedLink>
                      ))}
                    </div>
                  </>
                ) : (
                  <LocalizedLink
                    to={item.href}
                    style={{
                      display: 'block',
                      padding: '16px 12px',
                      fontSize: '16px',
                      fontWeight: isActive(item.href) ? 600 : 500,
                      color: isActive(item.href) ? '#003b7a' : '#1e293b',
                      textDecoration: 'none',
                      borderRadius: '12px',
                      backgroundColor: isActive(item.href) ? 'rgba(0, 163, 204, 0.06)' : 'transparent',
                    }}
                  >
                    {item.label}
                  </LocalizedLink>
                )}
              </div>
            ))}
          </div>
        </div>
      </header>
    </>
  )
}
